import {
	getHerdQueryKey,
	getHerdQueryOptions,
	getLivestockSettingsQueryOptions,
	listAnimalsByOrganizationQueryOptions,
	listLivestockMeasurementsByOrganizationQueryOptions,
	listLivestockOperationsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type { ListLivestockOperationsByOrganization200 } from "@packages/api-client/types/livestockController";
import { Action } from "@packages/shared-types";
import type { LivestockAnimalWithHerd } from "@packages/shared-types/livestock";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRightLeft, Plus, Scale } from "lucide-react";
import {
	type ReactNode,
	type RefObject,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
	getLivestockHerdKpisQueryOptions,
	getLivestockPaddockRotationStatsQueryOptions,
} from "@/api/livestock/kpi";
import {
	AttentionDetailDialog,
	type AttentionEntity,
} from "@/components/livestock/attention/AttentionDetailDialog";
import {
	formatAttentionDuration,
	livestockAttentionForHerdQuery,
	type PaddockOverstayAttentionItem,
} from "@/components/livestock/attention/livestockAttention";
import { LivestockEntityDelete } from "@/components/livestock/LivestockEntityDelete";
import { OfflineActionGuard } from "@/components/OfflineActionGuard";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import { RecordsSection } from "@/components/registries/RecordsSection";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DetailSkeleton } from "@/components/ui/skeleton";
import { useHasPermission } from "@/hooks/useAccessControl";
import { getOfflineAnimalRows } from "@/hooks/useAnimalOptions";
import { getOfflineHerdEntry } from "@/hooks/useHerdsListData";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useOfflineGuard } from "@/hooks/useOfflineGuard";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { loadUnlessOffline } from "@/lib/offline/loadUnlessOffline";
import { LotResultBlock } from "../../-components/LotResultBlock";
import { RegisterPicker } from "../../-components/RegisterPicker";
import { RegistriesTab } from "../../-components/RegistriesTab";
import { SellAnimalsDialog } from "../../-components/SellAnimalsDialog";
import { HerdAnimalsCard } from "./-components/HerdAnimalsCard";
import { HerdAttentionCard } from "./-components/HerdAttentionCard";
import { HerdDetailOffline } from "./-components/HerdDetailOffline";
import {
	HerdBadges,
	HerdDetailsCard,
	HerdEditLink,
	HerdIdentityCard,
	HerdQuickRow,
	HerdQuickRowsCard,
	useHerdDetailRows,
} from "./-components/HerdIdentityCard";
import {
	HerdMobileEditButton,
	HerdMobileTopBar,
} from "./-components/HerdMobileChrome";
import { HerdPerformanceCard } from "./-components/HerdPerformanceCard";
import { RotatePaddockDialog } from "./-components/RotatePaddockDialog";

type OperationWithDetails =
	ListLivestockOperationsByOrganization200["data"][number];

const DESKTOP_QUERY = "(min-width: 1024px)";

function AnchoredSection({
	sectionRef,
	children,
}: {
	sectionRef?: RefObject<HTMLDivElement | null>;
	children: ReactNode;
}) {
	return (
		<div ref={sectionRef} className="scroll-mt-4">
			{children}
		</div>
	);
}

function scrollTo(ref: RefObject<HTMLDivElement | null>) {
	return () =>
		ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/herds/$herdId/",
)({
	staticData: { offline: "ready" },
	loader: ({ context, params }) => {
		const { orgId, establishmentId, herdId } = params;
		return loadUnlessOffline(async () => {
			await Promise.all([
				context.queryClient.ensureQueryData(getHerdQueryOptions(orgId, herdId)),
				context.queryClient.ensureQueryData(
					listAnimalsByOrganizationQueryOptions(orgId, {
						establishmentId,
						herdId,
						limit: "1000",
						includeLatestWeight: "true",
					}),
				),
				context.queryClient.ensureQueryData(
					listLivestockOperationsByOrganizationQueryOptions(orgId, {
						establishmentId,
						herdId,
					}),
				),
				context.queryClient.ensureQueryData(
					listLivestockMeasurementsByOrganizationQueryOptions(orgId, {
						establishmentId,
						herdId,
					}),
				),
				context.queryClient.ensureQueryData(
					livestockAttentionForHerdQuery(
						orgId,
						{ kind: "single", establishmentId },
						herdId,
					),
				),
			]);
		});
	},
	pendingComponent: DetailSkeleton,
	component: HerdDetailsPage,
});

function HerdDetailsPage() {
	const { orgId, establishmentId, herdId } = Route.useParams();
	const online = useOnlineStatus();
	const queryClient = useQueryClient();

	if (online) return <HerdDetailsFull />;

	const herdState = queryClient.getQueryState(getHerdQueryKey(orgId, herdId));
	const prepared = getOfflineHerdEntry(
		queryClient,
		orgId,
		establishmentId,
		herdId,
	);
	const preparedIsNewer =
		!!prepared && prepared.preparedAt > (herdState?.dataUpdatedAt ?? 0);
	if (herdState?.data && !preparedIsNewer) return <HerdDetailsFull />;
	if (!prepared) return <OfflineUnavailable variant="preparable" />;
	const animals = (
		getOfflineAnimalRows(queryClient, orgId, establishmentId) ?? []
	).filter((row) => row.herdId === herdId);
	return <HerdDetailOffline herd={prepared.row} animals={animals} />;
}

/**
 * The herd page, matching the animal-detail arrangement:
 *
 *  - Desktop — a 300px identity rail beside a stack of Weight, Attention,
 *    Animals and Records. No tabs: a manager should never have to guess
 *    which tab holds the number.
 *  - Mobile — one column under a 56px identity bar, the numbers first and
 *    the profile last, with Sell / New record pinned to a sticky bar.
 */
function HerdDetailsFull() {
	const { orgId, establishmentId, herdId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const checkPermission = useHasPermission();
	const canManageLivestock = checkPermission(Action.MANAGE_LIVESTOCK);
	const isDesktop = useMediaQuery(DESKTOP_QUERY);
	const { blocked } = useOfflineGuard();
	const [sellOpen, setSellOpen] = useState(false);
	const [showExtraFacts, setShowExtraFacts] = useState(false);
	const [attentionEntity, setAttentionEntity] =
		useState<AttentionEntity | null>(null);
	const animalsRef = useRef<HTMLDivElement>(null);
	const lotResultRef = useRef<HTMLDivElement>(null);
	const sellMode = useMemo(() => ({ kind: "herd" as const, herdId }), [herdId]);

	const { data: livestockSettings } = useQuery(
		getLivestockSettingsQueryOptions(orgId),
	);

	const { data: herd } = useSuspenseQuery(getHerdQueryOptions(orgId, herdId));

	const { data: animalsData } = useSuspenseQuery(
		listAnimalsByOrganizationQueryOptions(orgId, {
			establishmentId,
			herdId,
			limit: "1000",
			includeLatestWeight: "true",
		}),
	);

	const { data: operationsData } = useSuspenseQuery(
		listLivestockOperationsByOrganizationQueryOptions(orgId, {
			establishmentId,
			herdId,
		}),
	);

	const { data: measurementsData } = useSuspenseQuery(
		listLivestockMeasurementsByOrganizationQueryOptions(orgId, {
			establishmentId,
			herdId,
		}),
	);

	const { data: herdKpis } = useQuery({
		...getLivestockHerdKpisQueryOptions(orgId, {
			establishmentId,
			herdIds: herdId,
		}),
		throwOnError: false,
	});
	const kpis = herdKpis?.data?.[0];

	const { data: rotationStats } = useQuery({
		...getLivestockPaddockRotationStatsQueryOptions(orgId, { establishmentId }),
		throwOnError: false,
	});
	const daysOnPaddock =
		herd?.daysOnPaddock ??
		(herd?.currentPaddockId
			? rotationStats?.data
					?.find((p) => p.paddockId === herd.currentPaddockId)
					?.currentHerds.find((h) => h.herdId === herdId)?.daysOnPaddock
			: undefined);

	const { data: attentionData } = useQuery({
		...livestockAttentionForHerdQuery(
			orgId,
			{ kind: "single", establishmentId },
			herdId,
		),
		throwOnError: false,
	});
	const attentionItems = attentionData?.items ?? [];
	const overstayItem = attentionItems.find(
		(item): item is PaddockOverstayAttentionItem =>
			item.category === "PADDOCK_OVERSTAY",
	);
	const overstayDetail = overstayItem?.detail;

	const animals = (animalsData?.data ?? []) as LivestockAnimalWithHerd[];
	const operations = (operationsData?.data ?? []) as OperationWithDetails[];
	const totalRecords =
		operations.length + (measurementsData?.data ?? []).length;
	const activeAnimalCount = animals.filter((a) => a.status === "ACTIVE").length;
	const canWrite = Boolean(herd?.isActive) && canManageLivestock;
	const canSell = canWrite && activeAnimalCount > 0;
	const canWeigh = canManageLivestock && activeAnimalCount > 0;
	const hasNotes = Boolean(herd?.description?.trim());

	const goToEdit = () =>
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId/edit",
			params: { orgId, establishmentId, herdId },
		});

	const goToWeigh = () =>
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/weighing",
			params: { orgId, establishmentId },
			search: { herd: herdId },
		});

	const goAfterDelete = () =>
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "herds", farm: establishmentId },
		});

	const herdDelete = canManageLivestock ? (
		<LivestockEntityDelete
			orgId={orgId}
			kind="herd"
			entityId={herdId}
			entityName={herd?.name ?? ""}
			blocked={blocked}
			blockedReason={
				animals.length > 0
					? t("livestock.herd.deleteConfirm.blocked", {
							count: animals.length,
						})
					: undefined
			}
			variant="overflow"
			onDeleted={goAfterDelete}
		/>
	) : null;

	const herdDeleteMenuItem = canManageLivestock ? (
		<LivestockEntityDelete
			orgId={orgId}
			kind="herd"
			entityId={herdId}
			entityName={herd?.name ?? ""}
			blocked={blocked}
			blockedReason={
				animals.length > 0
					? t("livestock.herd.deleteConfirm.blocked", {
							count: animals.length,
						})
					: undefined
			}
			variant="menu-item"
			onDeleted={goAfterDelete}
		/>
	) : null;

	const editLink = canManageLivestock
		? (label: string) => (
				<HerdEditLink
					orgId={orgId}
					establishmentId={establishmentId}
					herdId={herdId}
					blocked={blocked}
					label={label}
				/>
			)
		: undefined;

	const paddockValue =
		herd?.currentPaddockId && herd.currentPaddockName ? (
			<>
				<Link
					to="/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId"
					params={{
						orgId,
						farmId: establishmentId,
						paddockId: herd.currentPaddockId,
					}}
					className="hover:underline"
				>
					{herd.currentPaddockName}
				</Link>
				{daysOnPaddock != null &&
					(overstayItem ? (
						<span className="ml-1.5 font-sans text-xs font-normal text-warning">
							{t("livestock.attention.overstayShort", {
								duration: formatAttentionDuration(
									overstayDetail?.daysOnPaddock ?? daysOnPaddock,
									t,
								),
								target: overstayDetail?.targetDays ?? 0,
							})}
						</span>
					) : (
						<span className="ml-1.5 font-sans text-xs font-normal text-base-content/60">
							{t("livestock.kpi.onPaddockDays", { count: daysOnPaddock })}
						</span>
					))}
			</>
		) : undefined;

	const paddockAction =
		herd && canManageLivestock ? (
			<RotatePaddockDialog
				orgId={orgId}
				establishmentId={establishmentId}
				herdId={herdId}
				currentPaddockId={herd.currentPaddockId}
				currentPaddockName={herd.currentPaddockName}
				currentRotationOpId={herd.currentRotationOpId}
				trigger={(openDialog) => (
					<OfflineActionGuard blocked={blocked}>
						<button
							type="button"
							aria-disabled={blocked}
							className="shrink-0 text-xs font-semibold text-primary hover:underline"
							onClick={openDialog}
						>
							{herd.currentPaddockId
								? t("livestock.herd.rotateToPaddockShort")
								: t("livestock.herd.assignPaddockShort")}
						</button>
					</OfflineActionGuard>
				)}
			/>
		) : undefined;

	const { rows, filled, total } = useHerdDetailRows({
		herd,
		animals,
		paddockValue,
		paddockAction,
	});

	if (!herd) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">{t("livestock.herd.notFound")}</p>
			</div>
		);
	}

	const newRecordPicker = (
		trigger: (open: () => void) => ReactNode,
	): ReactNode => (
		<RegisterPicker
			orgId={orgId}
			establishmentId={establishmentId}
			scope={{ herdId }}
			blocked={blocked}
			trigger={(open) => trigger(() => open())}
		/>
	);

	const weighAction = canWeigh ? (
		<Button type="button" size="sm" onClick={goToWeigh}>
			<Scale className="mr-2 h-4 w-4" aria-hidden />
			{t("livestock.herd.detail.weight.weigh")}
		</Button>
	) : undefined;

	const performanceCard = (
		<HerdPerformanceCard
			dryMatterIntakePercent={livestockSettings?.dryMatterIntakePercent}
			waterIntakePercent={livestockSettings?.waterIntakePercent}
			averageWeightKg={herd.averageWeightKg}
			targetWeightKg={herd.targetWeightKg}
			kpis={kpis}
			weighAction={weighAction}
		/>
	);

	const attentionCard = (
		<HerdAttentionCard
			orgId={orgId}
			herdId={herdId}
			attentionItems={attentionItems}
		/>
	);

	const animalsCard = (
		<AnchoredSection sectionRef={animalsRef}>
			<HerdAnimalsCard
				orgId={orgId}
				establishmentId={establishmentId}
				herdId={herdId}
				animals={animals}
				attentionCount={attentionItems.length}
				onAttentionClick={() =>
					setAttentionEntity({ type: "herd", id: herdId, name: herd.name })
				}
				onAnimalAttentionClick={(animal) =>
					setAttentionEntity({
						type: "animal",
						id: animal.id,
						name: animal.tagId,
					})
				}
			/>
		</AnchoredSection>
	);

	const recordsCard = (
		<RecordsSection
			title={t("livestock.herd.detail.records.title")}
			count={totalRecords}
			hint={t("livestock.herd.detail.records.hint")}
			action={
				canWrite &&
				newRecordPicker((open) => (
					<OfflineActionGuard blocked={blocked}>
						<Button
							type="button"
							size="sm"
							aria-disabled={blocked}
							onClick={open}
						>
							<Plus className="mr-2 h-4 w-4" aria-hidden />
							{t("livestock.registries.register.button")}
						</Button>
					</OfflineActionGuard>
				))
			}
		>
			<RegistriesTab
				orgId={orgId}
				establishmentId={establishmentId}
				scope={{ herdId }}
				hideFirstUseCtas
			/>
		</RecordsSection>
	);

	const lotResultSection = (
		<AnchoredSection sectionRef={lotResultRef}>
			<LotResultBlock orgId={orgId} herdId={herdId} />
		</AnchoredSection>
	);

	const extraFacts = hasNotes && (
		<div>
			<button
				type="button"
				onClick={() => setShowExtraFacts((open) => !open)}
				aria-expanded={showExtraFacts}
				className="text-[13px] font-semibold text-primary hover:underline"
			>
				{t(showExtraFacts ? "common.showLess" : "common.showMore")}
			</button>
			{showExtraFacts && (
				<div className="mt-2.5">
					<div className="text-xs font-medium text-base-content/60">
						{t("livestock.herd.description")}
					</div>
					<p className="mt-1 whitespace-pre-line text-sm text-base-content">
						{herd.description}
					</p>
				</div>
			)}
		</div>
	);

	const profileFooter = (
		<>
			{filled < total && (
				<p className="text-[13px] text-base-content/60">
					{t("livestock.herd.detail.details.fieldsFilled", {
						filled,
						total,
					})}{" "}
					{editLink?.(t("livestock.herd.detail.details.completeDetails"))}
				</p>
			)}
			{extraFacts}
		</>
	);

	const quickRows = (
		<>
			<HerdQuickRow
				title={t("livestock.herd.detail.animals.title")}
				caption={t("livestock.herd.detail.animals.caption", {
					count: animals.length,
				})}
				onOpen={scrollTo(animalsRef)}
			/>
			<HerdQuickRow
				title={t("livestock.lotResult.title")}
				caption={t("livestock.herd.detail.lotResult.open")}
				onOpen={scrollTo(lotResultRef)}
			/>
		</>
	);

	const dialogs = (
		<>
			<SellAnimalsDialog
				open={sellOpen}
				onOpenChange={setSellOpen}
				orgId={orgId}
				establishmentId={establishmentId}
				mode={sellMode}
			/>
			<AttentionDetailDialog
				orgId={orgId}
				scope={{ kind: "single", establishmentId }}
				entity={attentionEntity}
				onOpenChange={(open) => !open && setAttentionEntity(null)}
			/>
		</>
	);

	if (isDesktop) {
		return (
			<div className="flex h-full flex-col gap-5 overflow-y-auto pb-10">
				<div className="flex items-center justify-between gap-4">
					<Breadcrumb
						items={[
							{
								label: t("livestock.nav.title"),
								to: "/app/org/$orgId/livestock/$establishmentId",
								params: { orgId, establishmentId },
							},
							{
								label: t("livestock.herd.list.title"),
								to: "/app/org/$orgId/livestock",
								params: { orgId },
								search: { tab: "herds", farm: establishmentId },
							},
							{ label: herd.name },
						]}
					/>
					<div className="flex shrink-0 items-center gap-2.5">
						{canManageLivestock && (
							<OfflineActionGuard blocked={blocked}>
								<Button
									type="button"
									variant="outline"
									size="touch"
									aria-disabled={blocked}
									onClick={goToEdit}
								>
									{t("common.edit")}
								</Button>
							</OfflineActionGuard>
						)}
						{canSell && (
							<OfflineActionGuard blocked={blocked}>
								<Button
									type="button"
									variant="accent"
									size="touch"
									aria-disabled={blocked}
									onClick={() => setSellOpen(true)}
								>
									{t("livestock.sales.sellCta")}
								</Button>
							</OfflineActionGuard>
						)}
						{herdDelete}
					</div>
				</div>

				<div className="grid grid-cols-[300px_minmax(0,1fr)] items-start gap-5">
					<HerdIdentityCard
						herd={herd}
						rows={rows}
						editLink={editLink}
						footer={profileFooter}
						quickRows={quickRows}
					/>
					<div className="flex min-w-0 flex-col gap-5">
						{performanceCard}
						{attentionCard}
						{animalsCard}
						{recordsCard}
						{lotResultSection}
					</div>
				</div>
				{dialogs}
			</div>
		);
	}

	// The purpose already sits in the badge row under the bar, next to the
	// status — the sub-line carries only the paddock.
	const subtitle = herd.currentPaddockName ?? t("livestock.herd.unassigned");
	const subtitleMuted = !herd.currentPaddockName;

	return (
		<div className="flex h-full flex-col overflow-y-auto">
			<HerdMobileTopBar
				title={herd.name}
				subtitle={subtitle}
				subtitleMuted={subtitleMuted}
				onBack={() => window.history.back()}
				editAction={
					canManageLivestock ? (
						<OfflineActionGuard blocked={blocked}>
							<HerdMobileEditButton onClick={goToEdit} blocked={blocked} />
						</OfflineActionGuard>
					) : null
				}
				menuItems={
					canManageLivestock ? (
						<>
							{canWeigh && (
								<Button
									type="button"
									variant="ghost"
									size="touch"
									className="justify-start"
									onClick={goToWeigh}
								>
									<Scale className="mr-2 h-4 w-4" aria-hidden />
									{t("livestock.herd.detail.weight.weigh")}
								</Button>
							)}
							<RotatePaddockDialog
								orgId={orgId}
								establishmentId={establishmentId}
								herdId={herdId}
								currentPaddockId={herd.currentPaddockId}
								currentPaddockName={herd.currentPaddockName}
								currentRotationOpId={herd.currentRotationOpId}
								trigger={(openDialog) => (
									<OfflineActionGuard blocked={blocked}>
										<Button
											type="button"
											variant="ghost"
											size="touch"
											className="justify-start"
											aria-disabled={blocked}
											onClick={openDialog}
										>
											<ArrowRightLeft className="mr-2 h-4 w-4" aria-hidden />
											{herd.currentPaddockId
												? t("livestock.herd.rotateToPaddock")
												: t("livestock.herd.assignPaddock")}
										</Button>
									</OfflineActionGuard>
								)}
							/>
							{herdDeleteMenuItem}
						</>
					) : undefined
				}
			/>

			<div className="flex flex-col gap-3.5 pb-4 pt-4">
				<HerdBadges herd={herd} />
				{performanceCard}
				{attentionCard}
				{animalsCard}
				{recordsCard}
				<HerdDetailsCard
					rows={rows}
					editLink={editLink}
					filledLabel={
						filled < total
							? t("livestock.herd.detail.details.fieldsFilledShort", {
									filled,
									total,
								})
							: undefined
					}
					action={
						<>
							{profileFooter}
							{filled < total && canManageLivestock && (
								<HerdEditLink
									orgId={orgId}
									establishmentId={establishmentId}
									herdId={herdId}
									blocked={blocked}
									label={t("livestock.herd.detail.details.completeDetails")}
									className="inline-flex h-11 w-full items-center justify-center rounded-md border border-base-300 text-sm font-medium text-base-content"
								/>
							)}
						</>
					}
				/>
				<HerdQuickRowsCard>{quickRows}</HerdQuickRowsCard>
				{lotResultSection}
			</div>

			{dialogs}
		</div>
	);
}
